import auth from '@/common/auth/auth';
import FILTER_TYPES from './filter-types';
import FilterDefinition from './filter-definition';
import {customFieldToFilterType} from './custom-fields';
import FilterOptions from './filter-options';
import PreferenceTypes from '@/store/preference-types';

/** @module FilterSet */

/**
 * @typedef {{source: string, path: string, labelBuilder: (function), value: string}} OptionsParams
 */

/**
 * @typedef {{type: string, options: OptionsParams, format: string}} FilterParams
 */

/**
 * Class to process filter specs
 */
export default class FilterSet {
    static delimiter = '/';

    filterSpec = null;

    filters = {};

    asyncDiscoveries = [];

    hasCustomFieldAccess = false;

    combinedFilters = null;

    query = [];

    defaultSortBy = null;

    /**
     * Prepare a filter set to use for the criteria from a set of definitions.
     * The definitions must be JSON pointers that are valid criteria path, and can
     * define custom field groups.
     * @param {string} id - filter set id (cache id)
     * @param {number} version - filter set version
     * @param {Vuex.Store} store - vuex store
     * @param {Object.<string, FilterParams>} filterSpec
     * @param {{}|[]} query
     * @param {string|null} defaultSortBy
     * @param {{}} store Define a store when using custom fields as part of the filter set
     */
    constructor({
        id, version, store, filterSpec, query = [], defaultSortBy = null,
    }) {
        this.id = id;
        this.version = version;
        this.hasCustomFieldAccess = auth.customFields.read.isAllowed;
        this.filterSpec = filterSpec;
        this.combinedFilters = null;
        this.store = store;
        this.query = query;
        this.initialSortBy = this.getInitialSort(defaultSortBy);
        this.defaultSortBy = defaultSortBy;
        this.traverseSet();
    }

    /**
     * Compute the value of the filter set sort state if defined.
     * @return {null|{}}
     */
    get sortBy() {
        if (this.defaultSortBy) {
            return this.initialSortBy || this.defaultSortBy;
        }
        return null;
    }

    /**
     * Determine the initial sort state for the filter from the user's preferences.
     * @param {null|{}} defaultSortBy
     * @return {null|{}}
     */
    getInitialSort(defaultSortBy) {
        const sortBy = this.store.getters['user/getUserPreference'](PreferenceTypes.sort, this.id);
        return sortBy || defaultSortBy;
    }

    /**
     * Save the sort preferences for this ID to the user's profile.
     * @param {{}} sortState
     * @return {Promise<void>}
     */
    async saveSortToPreferences(sortState) {
        this.store.dispatch('user/setUserPreference', {
            type: PreferenceTypes.sort,
            id: this.id,
            data: sortState,
        });
    }

    /**
     * Traverse the set definition and parse each filter row.
     */
    traverseSet() {
        Object.keys(this.filterSpec).forEach((jsonPointer) => {
            const filter = this.filterSpec[jsonPointer];
            this.parseFilter(jsonPointer, filter);
        });
    }

    /**
     * Parse the filter object to determine if it's static or requires an async
     * discovery of custom fields for a resource type.
     * @param {string} jsonPointer
     * @param {string} type
     * @param {OptionsParams|boolean} options
     * @param {string|boolean} format
     * @param {string|null} label
     * @throws {Error}
     */
    parseFilter(jsonPointer, {
        type, options = false, format = false, label = null,
    }) {
        if (!Object.keys(FILTER_TYPES).includes(type)) {
            throw new Error(`Filter type ${type} does not exist, FilterSet ${this.id}`);
        }
        if (type === FILTER_TYPES.custom) {
            if (!this.store) {
                // the store is needed to fetch custom fields and cache them
                throw new Error(`No store was provided for custom fields, FilterSet ${this.id}`);
            }
            if (!this.hasCustomFieldAccess) {
                // lack of permissions for custom fields
                return;
            }
            this.prepareAsyncDiscovery(jsonPointer, options);
        } else {
            const payload = {type, path: jsonPointer, label};
            if (format) {
                payload.format = format;
            }
            if (options !== false) {
                payload.options = new FilterOptions(options);
            }
            this.filters[jsonPointer] = new FilterDefinition(payload);
        }
    }

    /**
     * Prepares an async discovery of custom fields for the resource type defined
     * within the provided JSON pointer.
     * @param {string} jsonPointer
     * @param {object} options
     * @throws {Error}
     */
    prepareAsyncDiscovery(jsonPointer, options) {
        if (!options || !options.resourceType) {
            throw new Error(`Options with resourceType must be specified for custom fields ${jsonPointer}`);
        }
        const {resourceType} = options;
        const discovery = async () => {
            const data = resourceType;
            // dispatch store to fetch the data
            await this.store.dispatch('rulesEngine/fetchCustomFields', data);
            const {fields} = this.store.getters['rulesEngine/customFields'](data);
            return {resourceType, customFields: fields, jsonPointer};
        };
        this.asyncDiscoveries.push(discovery);
    }

    /**
     * Parses the async discoveries and returns a set of JSON pointers with the definition
     * of each custom field within.
     * @param {Object} discoveries
     * @param {Object} discovery
     * @returns {Object}
     */
    static parseDiscoveries(discoveries, discovery) {
        const {customFields} = discovery;
        if (customFields.length) {
            customFields.forEach(({fields}) => {
                const jsonPointer = `${discovery.jsonPointer}${FilterSet.delimiter}${fields.name}`;
                const definition = {
                    path: jsonPointer,
                    label: fields.name,
                    type: customFieldToFilterType(fields.type),
                    format: null, // TODO get format if required
                    customFieldFormat: fields.type,
                    additionalSchema: fields.additionalSchema,
                };
                discoveries[jsonPointer] = new FilterDefinition(definition);
            });
        }
        return discoveries;
    }

    /**
     * Asynchronously return the filters defined within instance of this set.
     * Triggers additional API calls if discoveries are required in this set.
     * @returns {Promise<{Object}>}
     */
    async getFilters() {
        if (this.combinedFilters) {
            return this.combinedFilters;
        }
        let discoveries = false;
        try {
            const results = await Promise.all(this.asyncDiscoveries.map(discovery => discovery()));
            discoveries = results.reduce(FilterSet.parseDiscoveries, {});
        } catch (error) {
            // TODO use sentry or similar to log this error
            discoveries = false;
        }
        let filters = {...this.filters};
        if (discoveries) {
            filters = {
                ...filters,
                ...discoveries,
            };
        }
        this.combinedFilters = filters;
        return this.combinedFilters;
    }
}
